/**
 * Countries with their ISO code, dial code and name.
 * Sorted with the most common SFA markets first, then alphabetical.
 * The list is intentionally compact — it covers every country with a
 * significant mobile user base.
 */
export const DIAL_CODES: { iso: string; dial: string; name: string }[] = [
  { iso: 'IN', dial: '+91', name: 'India' },
  { iso: 'US', dial: '+1', name: 'United States' },
  { iso: 'GB', dial: '+44', name: 'United Kingdom' },
  { iso: 'AE', dial: '+971', name: 'UAE' },
  { iso: 'SA', dial: '+966', name: 'Saudi Arabia' },
  { iso: 'SG', dial: '+65', name: 'Singapore' },
  { iso: 'AU', dial: '+61', name: 'Australia' },
  { iso: 'CA', dial: '+1', name: 'Canada' },
  { iso: 'DE', dial: '+49', name: 'Germany' },
  { iso: 'FR', dial: '+33', name: 'France' },
  { iso: 'IT', dial: '+39', name: 'Italy' },
  { iso: 'ES', dial: '+34', name: 'Spain' },
  { iso: 'NL', dial: '+31', name: 'Netherlands' },
  { iso: 'BR', dial: '+55', name: 'Brazil' },
  { iso: 'MX', dial: '+52', name: 'Mexico' },
  { iso: 'JP', dial: '+81', name: 'Japan' },
  { iso: 'KR', dial: '+82', name: 'South Korea' },
  { iso: 'CN', dial: '+86', name: 'China' },
  { iso: 'HK', dial: '+852', name: 'Hong Kong' },
  { iso: 'ID', dial: '+62', name: 'Indonesia' },
  { iso: 'MY', dial: '+60', name: 'Malaysia' },
  { iso: 'TH', dial: '+66', name: 'Thailand' },
  { iso: 'PH', dial: '+63', name: 'Philippines' },
  { iso: 'VN', dial: '+84', name: 'Vietnam' },
  { iso: 'BD', dial: '+880', name: 'Bangladesh' },
  { iso: 'PK', dial: '+92', name: 'Pakistan' },
  { iso: 'LK', dial: '+94', name: 'Sri Lanka' },
  { iso: 'NP', dial: '+977', name: 'Nepal' },
  { iso: 'ZA', dial: '+27', name: 'South Africa' },
  { iso: 'NG', dial: '+234', name: 'Nigeria' },
  { iso: 'KE', dial: '+254', name: 'Kenya' },
  { iso: 'GH', dial: '+233', name: 'Ghana' },
  { iso: 'EG', dial: '+20', name: 'Egypt' },
  { iso: 'TR', dial: '+90', name: 'Turkey' },
  { iso: 'RU', dial: '+7', name: 'Russia' },
  { iso: 'PL', dial: '+48', name: 'Poland' },
  { iso: 'SE', dial: '+46', name: 'Sweden' },
  { iso: 'NO', dial: '+47', name: 'Norway' },
  { iso: 'DK', dial: '+45', name: 'Denmark' },
  { iso: 'FI', dial: '+358', name: 'Finland' },
  { iso: 'CH', dial: '+41', name: 'Switzerland' },
  { iso: 'AT', dial: '+43', name: 'Austria' },
  { iso: 'BE', dial: '+32', name: 'Belgium' },
  { iso: 'PT', dial: '+351', name: 'Portugal' },
  { iso: 'IE', dial: '+353', name: 'Ireland' },
  { iso: 'NZ', dial: '+64', name: 'New Zealand' },
  { iso: 'IL', dial: '+972', name: 'Israel' },
  { iso: 'QA', dial: '+974', name: 'Qatar' },
  { iso: 'KW', dial: '+965', name: 'Kuwait' },
  { iso: 'BH', dial: '+973', name: 'Bahrain' },
  { iso: 'OM', dial: '+968', name: 'Oman' },
  { iso: 'JO', dial: '+962', name: 'Jordan' },
  { iso: 'LB', dial: '+961', name: 'Lebanon' },
  { iso: 'AR', dial: '+54', name: 'Argentina' },
  { iso: 'CL', dial: '+56', name: 'Chile' },
  { iso: 'CO', dial: '+57', name: 'Colombia' },
  { iso: 'PE', dial: '+51', name: 'Peru' },
  { iso: 'TZ', dial: '+255', name: 'Tanzania' },
  { iso: 'UG', dial: '+256', name: 'Uganda' },
  { iso: 'ET', dial: '+251', name: 'Ethiopia' },
  { iso: 'MM', dial: '+95', name: 'Myanmar' },
];

/**
 * Split a stored E.164 mobile number (e.g. "+919900110001") into
 * { dial, local } by matching against known dial codes.
 * Tries longest codes first so "+971…" beats "+97…".
 * Falls back to defaultDial if nothing matches.
 */
export function splitMobile(
  full: string | null | undefined,
  defaultDial = '+91',
): { dial: string; local: string } {
  if (!full) return { dial: defaultDial, local: '' };

  // Sort dial codes longest first so +971 is tried before +97, +7, etc.
  const sorted = [...DIAL_CODES]
    .map((c) => c.dial)
    // Deduplicate (US and CA both use +1)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort((a, b) => b.length - a.length);

  for (const dial of sorted) {
    if (full.startsWith(dial)) {
      return { dial, local: full.slice(dial.length) };
    }
  }
  return { dial: defaultDial, local: full.replace(/^\+/, '') };
}
